import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

// Lazy-load Tone.js to avoid AudioContext creation on import
let Tone = null;

/**
 * Lazily initialize Tone.js module
 * This prevents AudioContext from being created until actually needed
 */
const getTone = async () => {
  if (!Tone) {
    Tone = await import('tone');
  }
  return Tone;
};

/**
 * Audio track mapping for 14 chapters
 * file: clean filename (no spaces/special chars for reliable URL loading)
 * display: human-readable track name for the UI
 */
const TRACKS = [
  { file: 'ch01-ufltit.mp3',                    display: 'ufltit' },                                    // Chapter 1
  { file: 'ch02-i-love-your-echoes.mp3',         display: 'I Love Your Echoes' },                       // Chapter 2
  { file: 'ch03-helix-cipher.mp3',               display: 'Helix Cipher (Spiral Variations)' },         // Chapter 3
  { file: 'ch04-portal-opening.mp3',             display: 'Portal Opening' },                            // Chapter 4
  { file: 'ch05-asc-spiral.mp3',                 display: 'ASC Spiral' },                                // Chapter 5
  { file: 'ch06-flow.mp3',                       display: 'Flow' },                                      // Chapter 6
  { file: 'ch07-mrflng.mp3',                     display: 'mrflng' },                                    // Chapter 7
  { file: 'ch08-lusher-looser-let-go.mp3',       display: 'Lusher Looser, Let Go' },                    // Chapter 8
  { file: 'ch09-cataclysmic-coil.mp3',           display: 'Cataclysmic Coil' },                         // Chapter 9
  { file: 'ch10-love-is-still-the-loudest.mp3',  display: 'Love Is Still the Loudest Thing Ever Made' },// Chapter 10
  { file: 'ch11-you-reverberate.mp3',            display: 'You Reverberate' },                           // Chapter 11
  { file: 'ch12-absolute-self-consistency.mp3',   display: 'Absolute Self-Consistency' },                // Chapter 12
  { file: 'ch13-notes-to-a-shattered-self.mp3',  display: 'Notes to a Simply Shattered Self' },         // Chapter 13
  { file: 'ch14-velvet-horizons.mp3',            display: 'Velvet Horizons' },                           // Chapter 14
];

/**
 * Main audio hook for the poem browser
 * Handles loading, playback, crossfading, and volume control
 *
 * @param {number} initialChapter - Starting chapter (0-13)
 * @param {string} audioBasePath - Base path to audio files (default: '../../../audio/')
 * @returns {Object} Audio control interface
 */
export const useAudio = (audioBasePath = '/audio/') => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolumeState] = useState(0.7); // 0-1 range
  const [error, setError] = useState(null);

  // Tracks what chapter is ACTUALLY LOADED/PLAYING (not what the caller wants).
  // Only updated after a successful play() or crossfadeToChapter().
  const playingChapterRef = useRef(null);

  // Audio players - we use two for crossfading
  const playerARef = useRef(null);
  const playerBRef = useRef(null);
  const currentPlayerRef = useRef('A'); // Track which player is active

  // Volume control nodes
  const volumeARef = useRef(null);
  const volumeBRef = useRef(null);
  const masterVolumeRef = useRef(null);

  // Track loaded state
  const loadedTracksRef = useRef(new Map());
  const isInitializedRef = useRef(false);
  const loadingTracksRef = useRef(new Set()); // Prevent concurrent loads

  /**
   * Initialize Tone.js audio context and create audio chain
   * This should only be called after a user gesture (click/touch)
   */
  const initializeAudio = useCallback(async () => {
    if (isInitializedRef.current) return;

    try {
      // Lazy-load Tone.js module (defers AudioContext creation)
      const ToneModule = await getTone();

      // Ensure AudioContext is running (required after user gesture)
      if (ToneModule.context.state !== 'running') {
        await ToneModule.start();
        console.log('Audio context started after user gesture');
      }

      // Create master volume control (use initial volume, update separately)
      masterVolumeRef.current = new ToneModule.Volume(
        ToneModule.gainToDb(0.7)
      ).toDestination();

      // Create volume controls for each player (for crossfading)
      volumeARef.current = new ToneModule.Volume(0).connect(masterVolumeRef.current);
      volumeBRef.current = new ToneModule.Volume(-Infinity).connect(masterVolumeRef.current);

      // Create players
      playerARef.current = new ToneModule.Player({
        loop: true,
        fadeIn: 0.5,
        fadeOut: 0.5,
        volume: 0
      }).connect(volumeARef.current);

      playerBRef.current = new ToneModule.Player({
        loop: true,
        fadeIn: 0.5,
        fadeOut: 0.5,
        volume: 0
      }).connect(volumeBRef.current);

      isInitializedRef.current = true;
    } catch (err) {
      console.error('Failed to initialize audio:', err);
      setError('Failed to initialize audio system');
    }
  }, []); // Remove volume dependency to prevent re-init

  /**
   * Load an audio file into a player
   */
  const loadTrack = useCallback(async (chapterIndex, player) => {
    if (chapterIndex < 0 || chapterIndex >= TRACKS.length) {
      throw new Error(`Invalid chapter index: ${chapterIndex}`);
    }

    const track = TRACKS[chapterIndex];
    const trackPath = `${audioBasePath}${track.file}`;

    // Get Tone module (should already be loaded at this point)
    const ToneModule = await getTone();

    // Check if already loaded
    if (loadedTracksRef.current.has(trackPath)) {
      const buffer = loadedTracksRef.current.get(trackPath);
      player.buffer = buffer;
      return buffer;
    }

    // Check if currently loading (prevent duplicate loads)
    if (loadingTracksRef.current.has(trackPath)) {
      console.log(`Track ${track.file} already loading, waiting...`);
      // Wait for the other load to complete
      while (loadingTracksRef.current.has(trackPath)) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Now it should be loaded
      if (loadedTracksRef.current.has(trackPath)) {
        const buffer = loadedTracksRef.current.get(trackPath);
        player.buffer = buffer;
        return buffer;
      }
    }

    // Mark as loading
    loadingTracksRef.current.add(trackPath);
    setIsLoading(true);

    try {
      await new Promise((resolve, reject) => {
        const buffer = new ToneModule.Buffer(trackPath, () => {
          player.buffer = buffer;
          loadedTracksRef.current.set(trackPath, buffer);
          loadingTracksRef.current.delete(trackPath);
          setIsLoading(false);
          resolve(buffer);
        }, (error) => {
          loadingTracksRef.current.delete(trackPath);
          setIsLoading(false);
          reject(error);
        });
      });

      console.log(`Loaded: ${track.display}`);
      return player.buffer;
    } catch (err) {
      loadingTracksRef.current.delete(trackPath);
      setIsLoading(false);
      throw new Error(`Failed to load ${track.display}: ${err.message}`);
    }
  }, [audioBasePath]);

  /**
   * Play a specific chapter
   * @param {number} chapterIndex - Chapter to play (0-13)
   */
  const play = useCallback(async (chapterIndex) => {
    try {
      if (chapterIndex < 0 || chapterIndex >= TRACKS.length) {
        console.error(`Invalid chapter index: ${chapterIndex}`);
        return;
      }

      // Guard: Don't allow overlapping play calls
      if (isLoading) {
        console.log('Already loading, skipping play()');
        return;
      }

      await initializeAudio();

      const activePlayer = currentPlayerRef.current === 'A'
        ? playerARef.current
        : playerBRef.current;

      // Guard: Check if player exists
      if (!activePlayer) {
        console.error('Player not initialized');
        return;
      }

      // If already playing this chapter, skip
      if (activePlayer.state === 'started' && playingChapterRef.current === chapterIndex) {
        return;
      }

      // If playing a different chapter, stop first
      if (activePlayer.state === 'started') {
        activePlayer.stop();
      }

      // Load the requested chapter's track
      await loadTrack(chapterIndex, activePlayer);

      // Final guard: Verify buffer loaded before starting
      if (activePlayer.buffer && activePlayer.buffer.loaded) {
        activePlayer.start();
        playingChapterRef.current = chapterIndex;
        setIsPlaying(true);
        console.log(`Playback started: ${TRACKS[chapterIndex].display}`);
      } else {
        console.error('Buffer not loaded after loadTrack');
        setError('Failed to load audio buffer');
      }
    } catch (err) {
      console.error('Play error:', err);
      setError(err.message);
    }
  }, [initializeAudio, loadTrack, isLoading]);

  /**
   * Pause playback
   */
  const pause = useCallback(() => {
    const currentPlayer = currentPlayerRef.current === 'A'
      ? playerARef.current
      : playerBRef.current;

    if (currentPlayer && currentPlayer.state === 'started') {
      currentPlayer.stop();
    }
    // Only update state if actually playing (avoid unnecessary re-renders)
    if (isPlaying) {
      setIsPlaying(false);
    }
  }, [isPlaying]);

  /**
   * Stop playback and reset
   */
  const stop = useCallback(() => {
    if (playerARef.current) playerARef.current.stop();
    if (playerBRef.current) playerBRef.current.stop();
    setIsPlaying(false);
  }, []);

  /**
   * Crossfade to a new chapter
   * @param {number} newChapterIndex - Index of the chapter to transition to
   * @param {number} duration - Crossfade duration in seconds (default: 2)
   */
  const crossfadeToChapter = useCallback(async (newChapterIndex, duration = 2) => {
    if (newChapterIndex < 0 || newChapterIndex >= TRACKS.length) {
      console.error(`Invalid chapter index: ${newChapterIndex}`);
      return;
    }

    if (newChapterIndex === playingChapterRef.current) return;

    // Guard: Don't crossfade if loading
    if (isLoading) {
      console.log('Currently loading, skipping crossfade');
      return;
    }

    try {
      await initializeAudio();

      // Determine which player is current and which is next
      const isPlayerACurrent = currentPlayerRef.current === 'A';
      const currentPlayer = isPlayerACurrent ? playerARef.current : playerBRef.current;
      const nextPlayer = isPlayerACurrent ? playerBRef.current : playerARef.current;
      const currentVolume = isPlayerACurrent ? volumeARef.current : volumeBRef.current;
      const nextVolume = isPlayerACurrent ? volumeBRef.current : volumeARef.current;

      // Guard: Verify players exist
      if (!currentPlayer || !nextPlayer) {
        console.error('Players not initialized');
        return;
      }

      // Load the new track into the inactive player
      await loadTrack(newChapterIndex, nextPlayer);

      // Guard: Verify buffer loaded
      if (!nextPlayer.buffer || !nextPlayer.buffer.loaded) {
        console.error('Next player buffer not loaded');
        return;
      }

      // Start the new player
      nextPlayer.start();

      // Get Tone module for timing
      const ToneModule = await getTone();

      // Crossfade: fade out current, fade in next
      const now = ToneModule.now();

      // Fade out current player
      currentVolume.volume.cancelScheduledValues(now);
      currentVolume.volume.setValueAtTime(currentVolume.volume.value, now);
      currentVolume.volume.linearRampToValueAtTime(-Infinity, now + duration);

      // Fade in next player
      nextVolume.volume.cancelScheduledValues(now);
      nextVolume.volume.setValueAtTime(-Infinity, now);
      nextVolume.volume.linearRampToValueAtTime(0, now + duration);

      // Schedule stop of old player after fade completes
      ToneModule.Transport.scheduleOnce(() => {
        if (currentPlayer && currentPlayer.state === 'started') {
          currentPlayer.stop();
        }
      }, `+${duration + 0.1}`);

      // Switch active player reference
      currentPlayerRef.current = isPlayerACurrent ? 'B' : 'A';
      playingChapterRef.current = newChapterIndex;
      setIsPlaying(true);

      console.log(`Crossfaded to chapter ${newChapterIndex}`);

    } catch (err) {
      console.error('Crossfade error:', err);
      setError(err.message);
    }
  }, [initializeAudio, loadTrack, isLoading]);

  /**
   * Set master volume
   * @param {number} newVolume - Volume level (0-1)
   */
  const setVolume = useCallback(async (newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);

    if (masterVolumeRef.current && Tone) {
      const dbValue = clampedVolume === 0 ? -Infinity : Tone.gainToDb(clampedVolume);
      masterVolumeRef.current.volume.rampTo(dbValue, 0.1);
    }
  }, []);

  /**
   * Get the current track name
   */
  const getCurrentTrackName = useCallback(() => {
    if (playingChapterRef.current === null) return 'Unknown';
    const track = TRACKS[playingChapterRef.current];
    return track ? track.display : 'Unknown';
  }, []);

  /**
   * Preload adjacent tracks for smoother transitions
   */
  const preloadAdjacentTracks = useCallback(async () => {
    const chapter = playingChapterRef.current;
    if (chapter === null) return;
    const nextChapter = (chapter + 1) % TRACKS.length;

    const inactivePlayer = currentPlayerRef.current === 'A'
      ? playerBRef.current
      : playerARef.current;

    try {
      if (nextChapter !== chapter) {
        await loadTrack(nextChapter, inactivePlayer);
      }
    } catch (err) {
      console.warn('Preload failed:', err);
    }
  }, [loadTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playerARef.current) {
        playerARef.current.dispose();
      }
      if (playerBRef.current) {
        playerBRef.current.dispose();
      }
      if (volumeARef.current) {
        volumeARef.current.dispose();
      }
      if (volumeBRef.current) {
        volumeBRef.current.dispose();
      }
      if (masterVolumeRef.current) {
        masterVolumeRef.current.dispose();
      }
      loadedTracksRef.current.clear();
      isInitializedRef.current = false;
    };
  }, []);

  /**
   * Get the currently active player for analysis
   */
  const getCurrentPlayer = useCallback(() => {
    return currentPlayerRef.current === 'A' ? playerARef.current : playerBRef.current;
  }, []);

  // Return a stable object reference using useMemo
  // This prevents infinite re-renders in consuming components
  return useMemo(() => ({
    // Playback controls
    play,
    pause,
    stop,
    crossfadeToChapter,

    // State
    isPlaying,
    isLoading,
    volume,
    error,

    // Utilities
    setVolume,
    getCurrentTrackName,
    getCurrentPlayer,
    preloadAdjacentTracks,

    // Refs for direct access (if needed)
    playerARef,
    playerBRef,
    currentPlayerRef,

    // Info
    totalChapters: TRACKS.length,
    tracks: TRACKS
  }), [
    play, pause, stop, crossfadeToChapter,
    isPlaying, isLoading, volume, error,
    setVolume, getCurrentTrackName, getCurrentPlayer, preloadAdjacentTracks
  ]);
};

export default useAudio;
